import ReactMarkdown from 'react-markdown';

import { ptBR } from '@/i18n/pt-BR';
import type { CriterionBandStatus } from '@/lib/case_workspace';
import { criterionStatusFrom } from '@/lib/criterion_status';
import { fmtLR, lrToSupportIndex } from '@/lib/lr';
import { Badge } from '@/components/ui/Badge';
import { Blueprint } from '@/components/ui/Blueprint';
import { cn } from '@/lib/utils';

/**
 * E13.F7b.T01: card "Síntese do Agente" reescrito como tabela de decomposição
 * + narrativa markdown abaixo.
 *
 * Ordem canônica das 10 chaves de critério segue `case_workspace_service.
 * CRITERION_KEYS` e o mapeamento `_CRITERION_NAME_KEYS` do CriteriaPanel.
 */
const CANONICAL_KEYS = [
  'calibre',
  'inclinacao',
  'proporcionalidade',
  'espacamento',
  'velocidade',
  'pressao',
  'ataque_remate',
  'minimos_graficos',
  'gestos_caracteristicos',
  'forma_global',
] as const;

const NAME_BY_KEY: Record<(typeof CANONICAL_KEYS)[number], keyof typeof ptBR.criterio.nome> = {
  calibre: 'calibre',
  inclinacao: 'inclinacao',
  proporcionalidade: 'proporcionalidade',
  espacamento: 'espacamento',
  velocidade: 'velocidade',
  pressao: 'pressao',
  ataque_remate: 'ataque',
  minimos_graficos: 'minimos',
  gestos_caracteristicos: 'gestos',
  forma_global: 'forma',
};

export type AgentDirection = 'up' | 'down' | 'neutral';

// E25.F3/ADR-0035: a direção da seta segue o status resolvido pelo backend
// (banda canônica [1/3, 3]) — a régua local 1,5/0,67 que vivia aqui era a
// 3ª cópia client-side (mesma classe da Crítica F-E16.F2-01).
const DIRECTION_BY_STATUS: Record<CriterionBandStatus, AgentDirection> = {
  convergent: 'up',
  divergent: 'down',
  neutral: 'neutral',
};

export function directionForStatus(
  criteriaStatus: Record<string, CriterionBandStatus> | null,
  key: string,
  lr: number | null,
): AgentDirection {
  if (lr === null || !Number.isFinite(lr)) return 'neutral';
  return DIRECTION_BY_STATUS[criterionStatusFrom(criteriaStatus, key)];
}

/**
 * Coluna de barra da prancha 2C (E54.F6.T09).
 *
 * A prancha a chama de "peso relativo". Aqui ela não pode se chamar assim: o
 * composto é média geométrica e nenhum critério pesa mais que outro (ADR-0023)
 * — rotular de peso inventaria uma hierarquia que o motor não tem. O que a
 * barra codifica é a FORÇA do próprio LR do critério, pela mesma transformação
 * do índice de suporte da mesa (`lrToSupportIndex`, ADR-0054): comprimento =
 * força, cor = direção. Divergência registrada em
 * docs/design/comparacoes/README.md.
 *
 * Critério sem LR no `criteria_breakdown` mostra o trilho vazio — não houve
 * avaliação, e desenhar comprimento ali seria desenhar o que não aconteceu.
 */
const _COR_DA_DIRECAO: Record<AgentDirection, string> = {
  up: 'bg-success',
  down: 'bg-danger',
  neutral: 'bg-accent',
};

function CriterionStrengthBar({
  lr,
  direction,
}: {
  lr: number | null;
  direction: AgentDirection;
}): JSX.Element {
  const index = lr === null ? null : lrToSupportIndex(lr);
  const largura = index === null ? 0 : Math.min(100, Math.abs(index));
  return (
    <span
      className="block h-1.5 w-full bg-fg/[0.08]"
      data-testid="agent-summary-strength"
      data-largura={largura}
    >
      {largura > 0 ? (
        <span
          className={cn(
            'block h-full transition-[width] duration-slow ease-sc',
            _COR_DA_DIRECAO[direction],
          )}
          style={{ width: `${largura}%` }}
        />
      ) : null}
    </span>
  );
}

/** Tag de direção — SEMPRE com texto: cor nunca é o único portador (pacote). */
const _VARIANTE_DA_DIRECAO: Record<AgentDirection, 'success' | 'danger' | 'neutral'> = {
  up: 'success',
  down: 'danger',
  neutral: 'neutral',
};

function DirectionTag({
  lr,
  status,
  direction,
}: {
  lr: number | null;
  status: CriterionBandStatus;
  direction: AgentDirection;
}): JSX.Element {
  const rotulo =
    lr === null ? ptBR.workspaceCaso.criteriaNoSignalBadge : ptBR.criterio.status[status];
  return (
    <Badge variant={lr === null ? 'neutral' : _VARIANTE_DA_DIRECAO[direction]}>{rotulo}</Badge>
  );
}

export interface AgentSummaryCardProps {
  criteriaBreakdown: Record<string, number> | null;
  /** E25.F3/ADR-0035: status por critério resolvido pelo backend. */
  criteriaStatus: Record<string, CriterionBandStatus> | null;
  narrative: string | null;
  className?: string;
}

export function AgentSummaryCard({
  criteriaBreakdown,
  criteriaStatus,
  narrative,
  className,
}: AgentSummaryCardProps) {
  return (
    <Blueprint
      as="section"
      data-testid="agent-summary"
      className={cn('agent-summary flex flex-col gap-sc-4', className)}
      aria-labelledby="agent-summary-title"
    >
      <h3 id="agent-summary-title" className="text-heading-sm text-fg">
        {ptBR.workspaceCaso.summaryTitle}
      </h3>
      <table
        className="w-full table-fixed border-collapse text-body-sm"
        data-testid="agent-summary-table"
      >
        <thead>
          <tr className="border-b border-border text-left text-fg-muted">
            <th scope="col" className="py-1.5 pr-3 font-medium">
              {ptBR.workspaceCaso.summaryColCriterio}
            </th>
            <th scope="col" className="py-1.5 pr-3 text-right font-medium">
              {ptBR.workspaceCaso.summaryColLR}
            </th>
            <th scope="col" className="py-1.5 pr-3 font-medium">
              {ptBR.workspaceCaso.summaryColForca}
            </th>
            <th scope="col" className="py-1.5 font-medium">
              {ptBR.workspaceCaso.summaryColDirecao}
            </th>
          </tr>
        </thead>
        <tbody>
          {CANONICAL_KEYS.map((key) => {
            const lr = criteriaBreakdown?.[key] ?? null;
            const dir = directionForStatus(criteriaStatus, key, lr);
            const status = criterionStatusFrom(criteriaStatus, key);
            const lrText = lr === null ? '—' : fmtLR(lr);
            return (
              <tr
                key={key}
                data-testid={`agent-summary-row-${key}`}
                data-direction={dir}
                className="border-b border-border/40 last:border-0"
              >
                <th scope="row" className="py-1.5 pr-3 text-left font-normal text-fg">
                  {ptBR.criterio.nome[NAME_BY_KEY[key]]}
                </th>
                <td className="py-1.5 pr-3 text-right font-mono text-fg">{lrText}</td>
                <td className="py-1.5 pr-3" aria-hidden="true">
                  <CriterionStrengthBar lr={lr} direction={dir} />
                </td>
                <td className="py-1.5 text-fg">
                  <DirectionTag lr={lr} status={status} direction={dir} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {narrative !== null && narrative.length > 0 ? (
        <div
          data-testid="agent-summary-narrative"
          className="prose prose-sm max-w-none text-fg [&_p]:my-2"
        >
          <ReactMarkdown>{narrative}</ReactMarkdown>
        </div>
      ) : null}
    </Blueprint>
  );
}
